import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Loader2, X } from 'lucide-react';

import { IesGrid, IesScene } from '../../pages/analytics/components/IesScene';
import { greyP5 } from '../../theme';
import { fetchLdcAndBuildWireframeWithGrid } from '../../util/ldc';

interface IesPreview3DModalProps {
  isOpen: boolean;
  onClose: () => void;
  iesUrl?: string;
  title: string;
  subtitle?: string;
  colorTemperature?: number;
}

const cctToColor = (cct?: number): string => {
  if (!cct) return '#FFD3A0';
  if (cct <= 3000) return '#FFB770';
  if (cct <= 3500) return '#FFD3A0';
  if (cct <= 4200) return '#FFF1D6';
  if (cct <= 5000) return '#F4F6FF';
  return '#D6E0FF';
};

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

type GridState = { status: 'idle' } | { status: 'loading' } | { status: 'ready'; grid: IesGrid } | { status: 'error' };

const IesPreview3DModal: React.FC<IesPreview3DModalProps> = ({
  isOpen,
  onClose,
  iesUrl,
  title,
  subtitle,
  colorTemperature,
}) => {
  const { t } = useTranslation();
  const dialogTitleId = 'ies-preview-3d-modal-title';
  const [state, setState] = useState<GridState>({ status: 'idle' });
  const [mountingHeight, setMountingHeight] = useState(8);
  const debouncedHeight = useDebounced(mountingHeight, 500);
  const [sceneRendered, setSceneRendered] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMountingHeight(8);
    setState({ status: 'idle' });
    setSceneRendered(false);
  }, [isOpen]);

  // Reset rendered flag whenever debouncedHeight changes so loader re-shows
  useEffect(() => {
    setSceneRendered(false);
  }, [debouncedHeight]);

  useEffect(() => {
    if (!isOpen || !iesUrl) return;
    let cancelled = false;
    setState({ status: 'loading' });
    fetchLdcAndBuildWireframeWithGrid(iesUrl)
      .then(({ grid }) => {
        if (!cancelled) setState({ status: 'ready', grid });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, iesUrl]);

  if (!isOpen) return null;

  const color = cctToColor(colorTemperature);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        className="w-full max-w-3xl rounded-lg overflow-hidden flex flex-col"
        style={{ backgroundColor: greyP5, height: '75vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 flex-shrink-0">
          <div className="min-w-0">
            <h2 id={dialogTitleId} className="text-lg font-semibold text-white truncate">
              {t('catalogue.preview3dTitle')}
            </h2>
            <p className="text-greyP2 text-xs truncate">
              {title}
              {subtitle ? ` · ${subtitle}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-greyP3 hover:text-white transition-colors flex-shrink-0 ml-3"
            aria-label={t('common.close') ?? 'Close'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-end px-4 py-2 bg-black border-b border-zinc-800 flex-shrink-0">
          <label
            className={`flex items-center gap-2 text-xs select-none ${
              state.status === 'ready' ? 'text-greyP2' : 'text-greyP3 opacity-50'
            }`}
          >
            <span className="whitespace-nowrap">
              {t('catalogue.preview3dMountingHeight')}:{' '}
              <span className="text-white font-medium">{mountingHeight} m</span>
            </span>
            <input
              type="range"
              min={2}
              max={20}
              step={0.5}
              value={mountingHeight}
              disabled={state.status !== 'ready'}
              onChange={(e) => setMountingHeight(parseFloat(e.target.value))}
              className="w-32 accent-green01 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            />
          </label>
        </div>

        {/* Body */}
        <div className="flex flex-col flex-1 min-h-0 bg-black">
          <div className="relative flex-1 min-h-[420px]">
            {state.status === 'ready' ? (
              <>
                <IesScene
                  grid={state.grid}
                  color={color}
                  wireframe
                  mountingHeight={debouncedHeight}
                  groundScale={10}
                  onFirstRender={() => setSceneRendered(true)}
                />
                {(!sceneRendered || mountingHeight !== debouncedHeight) && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-none">
                    <Loader2 size={28} className="text-white animate-spin mb-2" />
                    <p className="text-white text-xs">{t('catalogue.preview3dLoading')}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                {state.status === 'loading' || state.status === 'idle' ? (
                  <>
                    <Loader2 size={28} className="text-greyP3 animate-spin mb-2" />
                    <p className="text-greyP3 text-xs">{t('catalogue.preview3dLoading')}</p>
                  </>
                ) : (
                  <>
                    <Box size={28} className="text-greyP3 mb-2" />
                    <p className="text-greyP3 text-xs">{t('catalogue.preview3dError')}</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IesPreview3DModal;
